import type { SpareCandidate } from './types'

export const getCandidateName = (c: SpareCandidate): string => {
  if (c.name_full) {
    return c.name_full
  }
  return [c.name_first, c.name_last].filter(Boolean).join(' ')
}

// Resolves a player_type_id to its display name (sport-specific). Supplied by
// the caller, which holds the sport's player-type list.
export type PositionNameResolver = (
  playerTypeId?: number | null
) => string | undefined

// Which field a sub-line came from, so the UI can pick a per-field icon.
export type CandidateSubLineKind = 'position' | 'skill' | 'gender' | 'age'

export interface CandidateSubLine {
  kind: CandidateSubLineKind
  text: string
}

// Defensive: build the row's sub-lines from whatever the payload provides.
// Order: position, gender, age, skill level. Missing fields are skipped, so a
// candidate with only a name renders just the name.
/**
 * The candidate's position name.
 *
 * Prefers `player_type_name`, which the listing endpoint already returns.
 * `resolvePositionName` maps the id via the Redux player-type list, which is
 * fetched asynchronously at app boot — so relying on it alone made the position
 * appear only if those types happened to have loaded first, and vanish
 * otherwise. The resolver stays as a fallback for older payloads that carry
 * only the id.
 */
export const getCandidatePosition = (
  c: SpareCandidate,
  resolvePositionName?: PositionNameResolver
): string | undefined =>
  c.player_type_name || resolvePositionName?.(c.player_type_id) || undefined

export const getCandidateSubLines = (
  c: SpareCandidate,
  resolvePositionName?: PositionNameResolver
): CandidateSubLine[] => {
  const lines: CandidateSubLine[] = []

  const position = getCandidatePosition(c, resolvePositionName)
  if (position) {
    lines.push({ kind: 'position', text: position })
  }

  if (c.gender) {
    lines.push({ kind: 'gender', text: c.gender })
  }

  if (c.age != null) {
    lines.push({ kind: 'age', text: `${c.age} yo` })
  }

  if (c.skill_level) {
    lines.push({ kind: 'skill', text: c.skill_level })
  }

  return lines
}
